// ============================================================================
// SESSION GRAPH TAB
// Knowledge graph view scoped to a single session, with merge support and
// a sync button to backfill session data into the graph.
// ============================================================================

import React, { useState } from 'react';
import { CheckCircle, AlertTriangle } from 'lucide-react';
import { CustomerResearchGraph } from './CustomerResearchGraph';
import { useAuth } from '../../contexts/AuthContext';

interface SessionGraphTabProps {
  projectId: string;
  sessionId: string;
}

type SyncResult = {
  type: 'success' | 'error';
  message: string;
  stats?: Record<string, number>;
  errors?: string[];
};

export const SessionGraphTab: React.FC<SessionGraphTabProps> = ({
  projectId,
  sessionId,
}) => {
  const { can } = useAuth();
  const canWrite = can('Workspace::Write');
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
  const [graphKey, setGraphKey] = useState(0);

  return (
    <div className="flex flex-col gap-6">
      <section className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="p-5 border-b border-slate-100">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-sm font-bold text-[#052831]">Session Graph</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Knowledge graph scoped to this session — phases, contributions, user stories, personas, decisions, and participants.
              </p>
              {canWrite && (
                <p className="text-xs text-slate-400 mt-1">
                  Use the <strong>Duplicates</strong> button in the graph toolbar to batch-detect and merge similar nodes, or select a node and right-click another to merge individually.
                </p>
              )}
            </div>
          </div>

          {syncResult && (
            <div className={`mt-3 p-3 rounded-lg text-xs flex items-start gap-2 ${
              syncResult.type === 'success'
                ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}>
              {syncResult.type === 'success'
                ? <CheckCircle size={14} className="shrink-0 text-emerald-600 mt-0.5" />
                : <AlertTriangle size={14} className="shrink-0 text-red-600 mt-0.5" />}
              <div>
                <p className="font-medium">{syncResult.message}</p>
                {syncResult.stats && (
                  <div className="flex flex-wrap gap-3 mt-1">
                    {Object.entries(syncResult.stats).map(([k, v]) => (
                      <span key={k}><strong>{v}</strong> {k}</span>
                    ))}
                  </div>
                )}
                {syncResult.errors && syncResult.errors.length > 0 && (
                  <details className="mt-1">
                    <summary className="cursor-pointer font-medium flex items-center gap-1">
                      <AlertTriangle size={11} /> {syncResult.errors.length} warning{syncResult.errors.length !== 1 ? 's' : ''}
                    </summary>
                    <ul className="mt-0.5 pl-3 list-disc space-y-0.5 text-amber-700">
                      {syncResult.errors.slice(0, 5).map((e) => <li key={e}>{e}</li>)}
                    </ul>
                  </details>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="p-5">
          <CustomerResearchGraph
            key={graphKey}
            projectId={projectId}
            sessionId={sessionId}
            allowMerge={canWrite}
          />
        </div>
      </section>
    </div>
  );
};
